/**
 * WebRTC Helper - browser broadcaster + viewer
 * Mesh topology: broadcaster opens 1 RTCPeerConnection per viewer.
 */

let cachedIceConfig = null;

export async function getIceConfig() {
    if (cachedIceConfig) return cachedIceConfig;
    try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/webrtc/ice-config`);
        cachedIceConfig = await res.json();
    } catch {
        cachedIceConfig = {
            iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
            has_turn: false,
            stun_only: true
        };
    }
    return cachedIceConfig;
}

export class WebRTCBroadcaster {
    constructor(matchId, mediaStream, callbacks = {}) {
        this.matchId = matchId;
        this.stream = mediaStream;
        this.peers = new Map(); // viewer_id -> RTCPeerConnection
        this.ws = null;
        this.callbacks = callbacks;
        this.iceConfig = null;
    }

    async connect() {
        this.iceConfig = await getIceConfig();
        const wsUrl = process.env.REACT_APP_BACKEND_URL.replace(/^https/, 'wss').replace(/^http/, 'ws');
        this.ws = new WebSocket(`${wsUrl}/api/webrtc/signal/${this.matchId}?role=broadcaster`);

        this.ws.onopen = () => this.callbacks.onStatus?.('signaling-connected');
        this.ws.onclose = () => this.callbacks.onStatus?.('signaling-closed');
        this.ws.onerror = () => this.callbacks.onStatus?.('signaling-error');

        this.ws.onmessage = async (msg) => {
            const data = JSON.parse(msg.data);
            switch (data.type) {
                case 'ready':
                    this.callbacks.onStatus?.('ready');
                    break;
                case 'viewer-joined':
                    await this.handleViewerJoined(data.viewer_id);
                    break;
                case 'answer':
                    await this.handleAnswer(data.viewer_id, data.sdp);
                    break;
                case 'ice-candidate':
                    await this.handleIceCandidate(data.viewer_id, data.candidate);
                    break;
                case 'viewer-left':
                    this.cleanupPeer(data.viewer_id);
                    this.callbacks.onViewerCount?.(this.peers.size);
                    break;
                default:
                    break;
            }
        };
    }

    async handleViewerJoined(viewerId) {
        const pc = new RTCPeerConnection(this.iceConfig);
        this.peers.set(viewerId, pc);

        // Add tracks
        this.stream.getTracks().forEach(track => pc.addTrack(track, this.stream));

        pc.onicecandidate = (e) => {
            if (e.candidate) {
                this.ws.send(JSON.stringify({
                    type: 'ice-candidate',
                    viewer_id: viewerId,
                    candidate: e.candidate
                }));
            }
        };

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        this.ws.send(JSON.stringify({
            type: 'offer',
            viewer_id: viewerId,
            sdp: pc.localDescription
        }));
        this.callbacks.onViewerCount?.(this.peers.size);
    }

    async handleAnswer(viewerId, sdp) {
        const pc = this.peers.get(viewerId);
        if (pc) await pc.setRemoteDescription(sdp);
    }

    async handleIceCandidate(viewerId, candidate) {
        const pc = this.peers.get(viewerId);
        if (pc && candidate) {
            try { await pc.addIceCandidate(candidate); } catch (e) {}
        }
    }

    cleanupPeer(viewerId) {
        const pc = this.peers.get(viewerId);
        if (pc) {
            pc.close();
            this.peers.delete(viewerId);
        }
    }

    disconnect() {
        for (const id of this.peers.keys()) this.cleanupPeer(id);
        if (this.ws) this.ws.close();
    }
}

export class WebRTCViewer {
    constructor(matchId, callbacks = {}) {
        this.matchId = matchId;
        this.pc = null;
        this.ws = null;
        this.callbacks = callbacks;
        this.iceConfig = null;
    }

    async connect() {
        this.iceConfig = await getIceConfig();
        const wsUrl = process.env.REACT_APP_BACKEND_URL.replace(/^https/, 'wss').replace(/^http/, 'ws');
        this.ws = new WebSocket(`${wsUrl}/api/webrtc/signal/${this.matchId}?role=viewer`);

        this.ws.onopen = () => this.callbacks.onStatus?.('signaling-connected');
        this.ws.onclose = () => this.callbacks.onStatus?.('signaling-closed');
        this.ws.onerror = () => this.callbacks.onStatus?.('signaling-error');

        this.ws.onmessage = async (msg) => {
            const data = JSON.parse(msg.data);
            switch (data.type) {
                case 'ready':
                    this.callbacks.onStatus?.(data.broadcaster_online ? 'awaiting-stream' : 'no-broadcaster');
                    break;
                case 'broadcaster-online':
                    this.callbacks.onStatus?.('awaiting-stream');
                    break;
                case 'broadcaster-offline':
                    this.cleanup();
                    this.callbacks.onStatus?.('no-broadcaster');
                    break;
                case 'offer':
                    await this.handleOffer(data.sdp);
                    break;
                case 'ice-candidate':
                    if (this.pc && data.candidate) {
                        try { await this.pc.addIceCandidate(data.candidate); } catch (e) {}
                    }
                    break;
                default:
                    break;
            }
        };
    }

    async handleOffer(sdp) {
        this.pc = new RTCPeerConnection(this.iceConfig);

        this.pc.ontrack = (e) => {
            this.callbacks.onStream?.(e.streams[0]);
            this.callbacks.onStatus?.('streaming');
        };

        this.pc.onicecandidate = (e) => {
            if (e.candidate) {
                this.ws.send(JSON.stringify({ type: 'ice-candidate', candidate: e.candidate }));
            }
        };

        await this.pc.setRemoteDescription(sdp);
        const answer = await this.pc.createAnswer();
        await this.pc.setLocalDescription(answer);
        this.ws.send(JSON.stringify({ type: 'answer', sdp: this.pc.localDescription }));
    }

    cleanup() {
        if (this.pc) {
            this.pc.close();
            this.pc = null;
        }
    }

    disconnect() {
        this.cleanup();
        if (this.ws) this.ws.close();
    }
}
